import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import type {
  AvatarProps,
  AvatarWrapperProps,
  AvatarImageProps,
  AvatarFallbackProps,
} from './types';
import { cn } from '../../lib/utils';

const AvatarWrapper = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Root>,
  AvatarWrapperProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    data-slot='avatar'
    className={cn(
      'eye:relative eye:flex eye:size-48 eye:items-center eye:justify-center eye:overflow-hidden eye:rounded-full eye:select-none',
      className,
    )}
    {...props}
  />
));
AvatarWrapper.displayName = 'AvatarWrapper';

const AvatarImage = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    data-slot='avatar-image'
    className={cn(
      'eye:aspect-square eye:size-full eye:object-cover',
      className,
    )}
    {...props}
  />
));
AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    data-slot='avatar-fallback'
    className={cn(
      'eye:flex eye:size-full eye:items-center eye:justify-center eye:rounded-full',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = 'AvatarFallback';

const Avatar = React.forwardRef<
  React.ComponentRef<typeof AvatarPrimitive.Image>,
  AvatarProps
>(({ children, src, onLoadingStatusChange, ...props }, ref) => (
  <AvatarWrapper {...props}>
    <AvatarImage
      ref={ref}
      src={src}
      onLoadingStatusChange={onLoadingStatusChange}
    />
    <AvatarFallback delayMs={src ? 600 : 0}>{children}</AvatarFallback>
  </AvatarWrapper>
));
Avatar.displayName = 'Avatar';

export { Avatar, AvatarWrapper, AvatarImage, AvatarFallback };
